import { useDashboardAudience } from './dashboardAudience'
import { useQuery } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { Download } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { ApiError, getJSON } from '@/lib/api'
import {
  portalActivityLabels,
  type PortalActivityListData,
  type PortalActivityListKind,
  type PortalActivityListResponse,
} from './portalActivityLists.types'
import { usePortalActivityListState } from './usePortalActivityListState'
import { usePortalActivityDownload } from './usePortalActivityDownload'
import { PortalActivityControls } from './PortalActivityControls'
import { PortalActivityTable } from './PortalActivityTable'
import { PortalPagination } from './PortalPagination'

export function MemberPortalActivityList({
  organizationId,
  viewer,
  kind,
}: {
  organizationId: number
  viewer: string
  kind: PortalActivityListKind
}) {
  const audience = useDashboardAudience(organizationId)
  const state = usePortalActivityListState(organizationId, kind)
  const query = useQuery({
    queryKey: [
      audience.preview ? 'organization-dashboard-preview' : 'member-portal-dashboard',
      viewer,
      organizationId,
      kind,
      state.path,
    ],
    queryFn: () => getJSON<PortalActivityListResponse>(state.path),
    retry: false,
  })
  const csv = usePortalActivityDownload(state.csvPath, `organization-${kind}.csv`)
  const data = query.isError ? undefined : query.data?.[kind]
  const labels = portalActivityLabels[kind]
  if (!audience.preview && (csv.forbidden || missingList(query.error)))
    return <Navigate to="/portal" replace />
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex flex-wrap items-baseline gap-1.5 text-body font-medium">
            <span>{labels.title}</span>{' '}
            {data && (
              <span className="text-caption font-normal text-muted-foreground">
                {kind === 'courses'
                  ? `· ${data.completed} completed · ${data.in_progress} in progress`
                  : `· ${data.total}`}
              </span>
            )}
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5"
            disabled={!data || csv.pending}
            onClick={() => void csv.download()}
          >
            <Download aria-hidden="true" className="size-3.5" />
            CSV
          </Button>
        </div>
        <PortalActivityControls
          kind={kind}
          params={state.params}
          change={state.change}
          data={data}
        />
      </div>
      {csv.error && <Alert variant="destructive">CSV unavailable. Try downloading again.</Alert>}
      {query.isPending && (
        <EmptyState variant="quiet" title={`Loading ${labels.title.toLowerCase()}`} />
      )}
      {query.isError && (
        <Alert variant="destructive">
          {labels.title} unavailable.{' '}
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Alert>
      )}
      {data && <ActivityResults data={data} kind={kind} state={state} />}
    </Card>
  )
}
function ActivityResults({
  data,
  kind,
  state,
}: {
  data: PortalActivityListData
  kind: PortalActivityListKind
  state: ReturnType<typeof usePortalActivityListState>
}) {
  return (
    <>
      {data.rows.length ? (
        <PortalActivityTable
          rows={data.rows}
          kind={kind}
          sort={state.params.get('sort')!}
          onSort={(sort) => state.change('sort', sort)}
        />
      ) : (
        <EmptyState
          variant="quiet"
          title={data.available_total ? 'No rows match' : portalActivityLabels[kind].empty}
          action={
            data.available_total ? (
              <Button variant="outline" onClick={state.clear}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      )}
      <PortalPagination
        pagination={data.pagination}
        onPage={(cursor) => state.change('cursor', cursor)}
      />
    </>
  )
}

function missingList(error: unknown) {
  return error instanceof ApiError && error.status === 404
}
